import { useMutation } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { buildServiceUrl } from "../../utils/urlBuilder";
import { useAuthRoles } from "../useAuthRoles";

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function toBlobFromWorker(res) {
  const body = res?.data ?? res;

  if (body instanceof Blob) return body;

  if (typeof body === "string") {
    return new Blob([body], { type: "application/octet-stream" });
  }

  if (body && typeof body === "object") {
    return new Blob([JSON.stringify(body, null, 2)], {
      type: "application/json",
    });
  }

  return new Blob([String(body ?? "")], { type: "application/octet-stream" });
}

export function useGetTransfer({ callApiWithWorker, t }) {
  const { isAdmin } = useAuthRoles();
  const connector = isAdmin ? "connectorMaster" : "connectorWorkerConsumer";

  return useMutation({
    mutationKey: ["getTransfer"],
    mutationFn: async ({
      identifier,
      datasetName,
      contractAgreementId,
      urlProvider,
      filename = "distribution.bin",
    }) => {
      if (!identifier) throw new Error("Missing distribution identifier");
      if (!datasetName) throw new Error("Missing datasetName");
      if (!contractAgreementId) throw new Error("Missing contractAgreementId");

      const transferType = "HttpData-PULL";
      const resolvedUrlProvider =
        urlProvider || new URL(buildServiceUrl("springConnector")).origin;
      const path =
        `/connector/start/transfer/read` +
        `?urlProvider=${encodeURIComponent(resolvedUrlProvider)}` +
        `&identifier=${encodeURIComponent(identifier)}` +
        `&transferType=${encodeURIComponent(transferType)}` +
        `&datasetName=${encodeURIComponent(datasetName)}` +
        `&contractAgreementId=${encodeURIComponent(contractAgreementId)}`;

      const res = await callApiWithWorker(path, {
        method: "GET",
        connector,
      });

      return {
        response: res,
        filename,
      };
    },
    onSuccess: async ({ response, filename }) => {
      const blob = toBlobFromWorker(response);
      downloadBlob(blob, filename);
    },
    onError: async (error) => {
      console.error("Contract transfer error:", error);

      await Swal.fire({
        title: t("dataOfferingsDetail.errorTitle") || "Error",
        text:
          t("dataOfferingsDetail.errorMessage") ||
          "An error occurred while processing your request.",
        icon: "error",
        confirmButtonText: t("profilePage.okButton") || "OK",
      });
    },
  });
}
